// Página de Templates de Notificação - Versão Refatorada
import React, { useState, useRef } from 'react'
import {
  Bell, Plus, Edit, Trash2, Filter, Mail, MessageSquare, Send,
  AlertCircle, Tag, Copy, Check
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Modal from '../components/Modal'
import { useToast } from '../components/Toast'
import { useAuth } from '../context/AuthContext'

import notificationTemplatesService, {
  NotificationTemplate,
  CreateTemplateData
} from '../services/notificationTemplates'
import estadosOSService, { EstadoOS } from '../services/estadosOS'

const NotificationsPage: React.FC = () => {
  const { success, error } = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Estados locais
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showTagsDropdown, setShowTagsDropdown] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null)
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null)
  const [filterTipo, setFilterTipo] = useState<'ALL' | 'WHATSAPP' | 'EMAIL' | 'MULTI'>('ALL')
  const templateInputRef = useRef<HTMLTextAreaElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Form state
  const [formData, setFormData] = useState<CreateTemplateData>({
    nome: '',
    tipo: 'WHATSAPP',
    evento: '',
    assunto: '',
    template: '',
    variaveis: [],
    estadosIds: [],
    enviarAutomatico: false,
    ativo: true
  })

  // Tags disponíveis
  const availableTags = [
    { tag: '{cliente}', description: 'Nome do cliente' },
    { tag: '{numero}', description: 'Número da OS/Venda' },
    { tag: '{numero_orcamento}', description: 'Número do orçamento' },
    { tag: '{equipamento}', description: 'Nome do equipamento' },
    { tag: '{problema}', description: 'Descrição do problema' },
    { tag: '{valor}', description: 'Preço do serviço' },
    { tag: '{valor_total}', description: 'Valor total' },
    { tag: '{validade}', description: 'Data de validade' },
    { tag: '{previsao}', description: 'Data de previsão' },
    { tag: '{data}', description: 'Data atual' },
    { tag: '{empresa}', description: 'Nome da empresa' },
    { tag: '{telefone}', description: 'Telefone da empresa' },
    { tag: '{endereco}', description: 'Endereço da empresa' },
    { tag: '{link_aprovacao}', description: 'Link para aprovação' }
  ]

  // Queries
  const { data: templates, isLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => notificationTemplatesService.getAll(),
    enabled: user?.tipo === 'ADMIN'
  })

  const { data: estados } = useQuery({
    queryKey: ['estados-os'],
    queryFn: () => estadosOSService.list(),
    enabled: user?.tipo === 'ADMIN'
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateTemplateData) => notificationTemplatesService.create(data),
    onSuccess: () => {
      success('Template criado com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
      handleCloseModal()
    },
    onError: (err: any) => {
      error('Erro ao criar template', err.message)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateTemplateData }) =>
      notificationTemplatesService.update(id, data),
    onSuccess: () => {
      success('Template atualizado com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
      handleCloseModal()
    },
    onError: (err: any) => {
      error('Erro ao atualizar template', err.message)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => notificationTemplatesService.delete(id),
    onSuccess: () => {
      success('Template deletado com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
      setShowDeleteModal(false)
      setDeletingTemplateId(null)
    },
    onError: (err: any) => {
      error('Erro ao deletar template', err.message)
    }
  })

  // Handlers
  const handleOpenModal = (template?: NotificationTemplate) => {
    if (template) {
      setEditingTemplate(template)
      setFormData({
        nome: template.nome,
        tipo: template.tipo,
        evento: template.evento,
        assunto: template.assunto || '',
        template: template.template,
        variaveis: template.variaveis ? JSON.parse(template.variaveis) : [],
        estadosIds: template.estadosIds ? JSON.parse(template.estadosIds) : [],
        enviarAutomatico: template.enviarAutomatico,
        ativo: template.ativo
      })
    } else {
      setEditingTemplate(null)
      setFormData({
        nome: '',
        tipo: 'WHATSAPP',
        evento: '',
        assunto: '',
        template: '',
        variaveis: [],
        estadosIds: [],
        enviarAutomatico: false,
        ativo: true
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingTemplate(null)
    setFormData({
      nome: '',
      tipo: 'WHATSAPP',
      evento: '',
      assunto: '',
      template: '',
      variaveis: [],
      estadosIds: [],
      enviarAutomatico: false,
      ativo: true
    })
  }

  const handleSave = () => {
    // Validações
    if (!formData.nome || !formData.tipo || !formData.evento || !formData.template) {
      error('Preencha todos os campos obrigatórios', 'Nome, Tipo, Evento e Template são obrigatórios')
      return
    }

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (id: number) => {
    setDeletingTemplateId(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    if (deletingTemplateId) {
      deleteMutation.mutate(deletingTemplateId)
    }
  }

  const handleToggleEstado = (estadoId: number) => {
    const currentEstados = formData.estadosIds || []
    const newEstados = currentEstados.includes(estadoId)
      ? currentEstados.filter(id => id !== estadoId)
      : [...currentEstados, estadoId]

    setFormData({ ...formData, estadosIds: newEstados })
  }

  const insertTag = (tag: string) => {
    if (templateInputRef.current) {
      const textarea = templateInputRef.current
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const currentTemplate = formData.template || ''

      // Inserir tag na posição do cursor
      const newTemplate = currentTemplate.substring(0, start) + tag + currentTemplate.substring(end)

      // Atualizar o formData
      setFormData({ ...formData, template: newTemplate })

      // Fechar dropdown
      setShowTagsDropdown(false)

      // Reposicionar o cursor após a tag
      setTimeout(() => {
        textarea.focus()
        const newPosition = start + tag.length
        textarea.setSelectionRange(newPosition, newPosition)
      }, 0)
    }
  }

  // Fechar dropdown ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTagsDropdown(false)
      }
    }

    if (showTagsDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTagsDropdown])


  const getEstadosNomes = (estadosIds: string | undefined) => {
    if (!estadosIds || !estados) return '-'
    try {
      const ids = JSON.parse(estadosIds) as number[]
      const nomes = ids
        .map(id => estados.find(e => e.id === id)?.nome)
        .filter(Boolean)
      return nomes.length > 0 ? nomes.join(', ') : '-'
    } catch {
      return '-'
    }
  }

  // Filtrar templates
  const filteredTemplates = templates?.filter(t => {
    if (filterTipo === 'ALL') return true
    return t.tipo === filterTipo
  })

  // Verificação de permissão
  if (user?.tipo !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Acesso Restrito</h2>
          <p className="text-gray-600">Apenas administradores podem configurar notificações.</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">A carregar templates...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Bell className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Templates de Notificação</h1>
              <p className="text-gray-600">Gerencie os templates de WhatsApp e Email</p>
            </div>
          </div>

          <Button
            onClick={() => handleOpenModal()}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Criar Novo Template
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-6">
        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filtrar por tipo:</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={filterTipo === 'ALL' ? 'primary' : 'outline'}
                onClick={() => setFilterTipo('ALL')}
              >
                Todos
              </Button>
              <Button
                size="sm"
                variant={filterTipo === 'WHATSAPP' ? 'primary' : 'outline'}
                onClick={() => setFilterTipo('WHATSAPP')}
                leftIcon={<MessageSquare className="h-4 w-4" />}
              >
                WhatsApp
              </Button>
              <Button
                size="sm"
                variant={filterTipo === 'EMAIL' ? 'primary' : 'outline'}
                onClick={() => setFilterTipo('EMAIL')}
                leftIcon={<Mail className="h-4 w-4" />}
              >
                Email
              </Button>
              <Button
                size="sm"
                variant={filterTipo === 'MULTI' ? 'primary' : 'outline'}
                onClick={() => setFilterTipo('MULTI')}
                leftIcon={<Send className="h-4 w-4" />}
              >
                Multi-canal
              </Button>
            </div>
          </div>
        </div>

        {/* Lista de Templates */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Evento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estados Associados
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates && filteredTemplates.length > 0 ? (
                filteredTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{template.nome}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        template.tipo === 'WHATSAPP' ? 'bg-green-100 text-green-800' :
                        template.tipo === 'EMAIL' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {template.tipo === 'WHATSAPP' && <MessageSquare className="h-3 w-3" />}
                        {template.tipo === 'EMAIL' && <Mail className="h-3 w-3" />}
                        {template.tipo === 'MULTI' && <Send className="h-3 w-3" />}
                        {template.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {template.evento}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {getEstadosNomes(template.estadosIds)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        template.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenModal(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Nenhum template encontrado. Clique em "Criar Novo Template" para começar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTemplate ? 'Editar Template' : 'Criar Novo Template'}
        size="xl"
      >
        <div className="space-y-6">
          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Template *
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Notificação de OS Aberta"
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Notificação *
            </label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="WHATSAPP">WhatsApp</option>
              <option value="EMAIL">Email</option>
              <option value="MULTI">Multi-canal (WhatsApp + Email)</option>
            </select>
          </div>

          {/* Evento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Evento *
            </label>
            <input
              type="text"
              value={formData.evento}
              onChange={(e) => setFormData({ ...formData, evento: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: ordemServicoAberta, orcamentoGerado, etc."
            />
          </div>

          {/* Assunto (se EMAIL ou MULTI) */}
          {(formData.tipo === 'EMAIL' || formData.tipo === 'MULTI') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assunto do Email
              </label>
              <input
                type="text"
                value={formData.assunto}
                onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: OS {numero} - Equipamento Recebido"
              />
            </div>
          )}

          {/* Template */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Mensagem do Template *
              </label>
              <div className="relative" ref={dropdownRef}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowTagsDropdown(!showTagsDropdown)}
                  leftIcon={<Tag className="h-3 w-3" />}
                >
                  Inserir Tag
                </Button>

                {showTagsDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    <div className="p-3 border-b border-gray-200 bg-gray-50">
                      <h4 className="font-semibold text-sm text-gray-700">Tags Disponíveis</h4>
                      <p className="text-xs text-gray-500 mt-1">Clique em uma tag para inseri-la no cursor</p>
                    </div>
                    <div className="p-2">
                      {availableTags.map((tagInfo) => (
                        <button
                          key={tagInfo.tag}
                          onClick={() => insertTag(tagInfo.tag)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-md transition-colors group"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <code className="text-sm font-mono text-blue-600 group-hover:text-blue-700">
                                {tagInfo.tag}
                              </code>
                              <p className="text-xs text-gray-600 mt-1">{tagInfo.description}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <textarea
              ref={templateInputRef}
              value={formData.template}
              onChange={(e) => setFormData({ ...formData, template: e.target.value })}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Digite a mensagem do template..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Use tags como {'{cliente}'}, {'{numero}'}, {'{valor}'} para dados dinâmicos
            </p>
          </div>

          {/* Estados */}
          {estados && estados.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estados Associados
              </label>
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                {estados.map((estado) => (
                  <label key={estado.id} className="flex items-center gap-2 py-1 hover:bg-gray-50 px-2 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.estadosIds?.includes(estado.id)}
                      onChange={() => handleToggleEstado(estado.id)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{estado.nome}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Enviar Automaticamente */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enviarAutomatico}
                onChange={(e) => setFormData({ ...formData, enviarAutomatico: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-700">
                Enviar automaticamente ao mudar para os estados selecionados
              </span>
            </label>
          </div>

          {/* Ativo */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-700">Template ativo</span>
            </label>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingTemplate ? 'Atualizar' : 'Criar'} Template
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar Exclusão"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Tem certeza que deseja deletar este template? Esta ação não pode ser desfeita.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              isLoading={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              Deletar
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  )
}

export default NotificationsPage
